/**
 * Theme Utility
 * Injects CSS variables from environment variables into the document root
 * This allows for dynamic theming across different brands/deployments
 */

export const injectThemeVariables = () => {
  const root = document.documentElement;

  // Brand Configuration
  const brandName = import.meta.env.VITE_BRAND_NAME || 'ScaleDino';
  document.title = import.meta.env.VITE_APP_TITLE || `${brandName} CRM`;

  // Primary Brand Colors
  root.style.setProperty('--brand-color', import.meta.env.VITE_BRAND_COLOR || '#02338A');
  root.style.setProperty('--brand-color-hover', import.meta.env.VITE_BRAND_COLOR_HOVER || '#01265c');
  root.style.setProperty('--brand-color-light', import.meta.env.VITE_BRAND_COLOR_LIGHT || '#0447b5');
  root.style.setProperty('--sky-blue', import.meta.env.VITE_SKY_BLUE || '#48cae4');
  root.style.setProperty('--sky-blue-hover', import.meta.env.VITE_SKY_BLUE_HOVER || '#32b5d6');

  // Login Page Colors
  root.style.setProperty('--login-bg-left', import.meta.env.VITE_LOGIN_BG_LEFT || '#023e8a');
  root.style.setProperty('--login-text-primary', import.meta.env.VITE_LOGIN_TEXT_PRIMARY || '#ffffff');
  root.style.setProperty('--login-text-secondary', import.meta.env.VITE_LOGIN_TEXT_SECONDARY || '#e0f2fe');
  root.style.setProperty('--login-bg-right-from', import.meta.env.VITE_LOGIN_BG_RIGHT_FROM || '#f8fafc');
  root.style.setProperty('--login-bg-right-via', import.meta.env.VITE_LOGIN_BG_RIGHT_VIA || '#ffffff');
  root.style.setProperty('--login-bg-right-to', import.meta.env.VITE_LOGIN_BG_RIGHT_TO || '#f1f5f9');
  root.style.setProperty('--login-button-bg-from', import.meta.env.VITE_LOGIN_BUTTON_BG_FROM || '#2563eb');
  root.style.setProperty('--login-button-bg-to', import.meta.env.VITE_LOGIN_BUTTON_BG_TO || '#1d4ed8');
  root.style.setProperty('--login-button-hover-from', import.meta.env.VITE_LOGIN_BUTTON_HOVER_FROM || '#1d4ed8');
  root.style.setProperty('--login-button-hover-to', import.meta.env.VITE_LOGIN_BUTTON_HOVER_TO || '#1e40af');

  // UI Component Colors
  root.style.setProperty('--primary-blue', import.meta.env.VITE_PRIMARY_BLUE || '#3b82f6');
  root.style.setProperty('--primary-blue-dark', import.meta.env.VITE_PRIMARY_BLUE_DARK || '#2563eb');
  root.style.setProperty('--primary-blue-light', import.meta.env.VITE_PRIMARY_BLUE_LIGHT || '#60a5fa');

  // Status Colors
  root.style.setProperty('--success-green', import.meta.env.VITE_SUCCESS_GREEN || '#10b981');
  root.style.setProperty('--success-green-dark', import.meta.env.VITE_SUCCESS_GREEN_DARK || '#059669');
  root.style.setProperty('--success-green-light', import.meta.env.VITE_SUCCESS_GREEN_LIGHT || '#34d399');

  root.style.setProperty('--danger-red', import.meta.env.VITE_DANGER_RED || '#ef4444');
  root.style.setProperty('--danger-red-dark', import.meta.env.VITE_DANGER_RED_DARK || '#dc2626');
  root.style.setProperty('--danger-red-light', import.meta.env.VITE_DANGER_RED_LIGHT || '#f87171');

  root.style.setProperty('--warning-amber', import.meta.env.VITE_WARNING_AMBER || '#f59e0b');
  root.style.setProperty('--warning-amber-dark', import.meta.env.VITE_WARNING_AMBER_DARK || '#d97706');
  root.style.setProperty('--warning-amber-light', import.meta.env.VITE_WARNING_AMBER_LIGHT || '#fbbf24');

  root.style.setProperty('--info-cyan', import.meta.env.VITE_INFO_CYAN || '#06b6d4');
  root.style.setProperty('--info-cyan-dark', import.meta.env.VITE_INFO_CYAN_DARK || '#0891b2');
  root.style.setProperty('--info-cyan-light', import.meta.env.VITE_INFO_CYAN_LIGHT || '#22d3ee');

  // Additional Colors
  root.style.setProperty('--purple', import.meta.env.VITE_PURPLE || '#8b5cf6');
  root.style.setProperty('--purple-dark', import.meta.env.VITE_PURPLE_DARK || '#7c3aed');
  root.style.setProperty('--purple-light', import.meta.env.VITE_PURPLE_LIGHT || '#a78bfa');
  root.style.setProperty('--pink', import.meta.env.VITE_PINK || '#ec4899');
  root.style.setProperty('--indigo', import.meta.env.VITE_INDIGO || '#6366f1');
  root.style.setProperty('--lime', import.meta.env.VITE_LIME || '#84cc16');
  root.style.setProperty('--orange', import.meta.env.VITE_ORANGE || '#f97316');

  // Gray Colors
  root.style.setProperty('--gray-50', import.meta.env.VITE_GRAY_50 || '#f9fafb');
  root.style.setProperty('--gray-100', import.meta.env.VITE_GRAY_100 || '#f3f4f6');
  root.style.setProperty('--gray-200', import.meta.env.VITE_GRAY_200 || '#e5e7eb');
  root.style.setProperty('--gray-300', import.meta.env.VITE_GRAY_300 || '#d1d5db');
  root.style.setProperty('--gray-400', import.meta.env.VITE_GRAY_400 || '#9ca3af');
  root.style.setProperty('--gray-500', import.meta.env.VITE_GRAY_500 || '#6b7280');
  root.style.setProperty('--gray-600', import.meta.env.VITE_GRAY_600 || '#4b5563');
  root.style.setProperty('--gray-700', import.meta.env.VITE_GRAY_700 || '#374151');
  root.style.setProperty('--gray-800', import.meta.env.VITE_GRAY_800 || '#1f2937');
  root.style.setProperty('--gray-900', import.meta.env.VITE_GRAY_900 || '#111827');

  // Border & Background Colors
  root.style.setProperty('--border-color', import.meta.env.VITE_BORDER_COLOR || '#A2A9B4');
  root.style.setProperty('--border-color-light', import.meta.env.VITE_BORDER_COLOR_LIGHT || '#e5e7eb');
  root.style.setProperty('--border-color-dark', import.meta.env.VITE_BORDER_COLOR_DARK || '#6b7280');
  root.style.setProperty('--bg-primary', import.meta.env.VITE_BG_PRIMARY || '#ffffff');
  root.style.setProperty('--bg-secondary', import.meta.env.VITE_BG_SECONDARY || '#f9fafb');
  root.style.setProperty('--bg-tertiary', import.meta.env.VITE_BG_TERTIARY || '#f3f4f6');

  // Text Colors
  root.style.setProperty('--text-primary', import.meta.env.VITE_TEXT_PRIMARY || '#1B2531');
  root.style.setProperty('--text-secondary', import.meta.env.VITE_TEXT_SECONDARY || '#6c757d');
  root.style.setProperty('--text-tertiary', import.meta.env.VITE_TEXT_TERTIARY || '#9ca3af');
  root.style.setProperty('--text-white', import.meta.env.VITE_TEXT_WHITE || '#ffffff');

  // Sidebar Colors
  root.style.setProperty('--sidebar-bg', import.meta.env.VITE_SIDEBAR_BG || '#02338A');
  root.style.setProperty('--sidebar-text', import.meta.env.VITE_SIDEBAR_TEXT || '#ffffff');
  root.style.setProperty('--sidebar-text-inactive', import.meta.env.VITE_SIDEBAR_TEXT_INACTIVE || '#ffffff99');
  root.style.setProperty('--sidebar-active-bg', import.meta.env.VITE_SIDEBAR_ACTIVE_BG || '#ffffff33');
  root.style.setProperty('--sidebar-hover-bg', import.meta.env.VITE_SIDEBAR_HOVER_BG || '#ffffff1a');

  // Pipeline Stage Colors
  root.style.setProperty('--stage-blue', import.meta.env.VITE_STAGE_BLUE || '#3b82f6');
  root.style.setProperty('--stage-green', import.meta.env.VITE_STAGE_GREEN || '#10b981');
  root.style.setProperty('--stage-amber', import.meta.env.VITE_STAGE_AMBER || '#f59e0b');
  root.style.setProperty('--stage-red', import.meta.env.VITE_STAGE_RED || '#ef4444');
  root.style.setProperty('--stage-purple', import.meta.env.VITE_STAGE_PURPLE || '#8b5cf6');
  root.style.setProperty('--stage-pink', import.meta.env.VITE_STAGE_PINK || '#ec4899');
  root.style.setProperty('--stage-cyan', import.meta.env.VITE_STAGE_CYAN || '#06b6d4');
  root.style.setProperty('--stage-lime', import.meta.env.VITE_STAGE_LIME || '#84cc16');
  root.style.setProperty('--stage-orange', import.meta.env.VITE_STAGE_ORANGE || '#f97316');
  root.style.setProperty('--stage-indigo', import.meta.env.VITE_STAGE_INDIGO || '#6366f1');

  // Legacy support for existing CSS variables
  root.style.setProperty('--gray-color', import.meta.env.VITE_TEXT_SECONDARY || '#6c757d');
  root.style.setProperty('--light', import.meta.env.VITE_TEXT_WHITE || '#fff');
  root.style.setProperty('--dark-border-color', import.meta.env.VITE_BORDER_COLOR || '#A2A9B4');
  root.style.setProperty('--black-color', import.meta.env.VITE_TEXT_PRIMARY || '#1B2531');
  root.style.setProperty('--theme-black', '#000000');
  root.style.setProperty('--theme-white', '#ffffff');
  root.style.setProperty('--theme-gray-100', import.meta.env.VITE_GRAY_100 || '#f3f4f6');
  root.style.setProperty('--theme-gray-200', import.meta.env.VITE_GRAY_200 || '#e5e7eb');
  root.style.setProperty('--theme-gray-300', import.meta.env.VITE_GRAY_300 || '#d1d5db');
  root.style.setProperty('--theme-gray-400', import.meta.env.VITE_GRAY_400 || '#9ca3af');

  console.log(`✅ Theme variables injected for: ${brandName}`);
};

/**
 * Get theme asset URLs from environment
 */
export const getThemeAssets = () => {
  return {
    brandName: import.meta.env.VITE_BRAND_NAME || 'ScaleDino',
    loginLogo: import.meta.env.VITE_LOGIN_LOGO_URL || 'https://media.scaledino.com/website/images/2x_newlanding_images/logo.png',
    sidebarLogo: import.meta.env.VITE_SIDEBAR_LOGO_URL || '/assets/dino.png',
    loginDecoration: import.meta.env.VITE_LOGIN_DECORATION_IMAGE || 'https://media.scaledino.com/website/images/2x_newlanding_images/rocket.png',
  };
};
